import { makeAutoObservable } from "mobx";
import { Quantity, RArray, naturalCompare, } from "../..";
import { PMoney } from "./PMoney";
import { PProductConfigCreator } from "./PProductConfigCreator";
import { PProductError } from "./PProductError";
import { PProductInstanceCreatorPart } from "./PProductInstanceCreatorPart";
import { PHalvesProductInstanceCreator, PSinglePartProductInstanceCreator, } from "./PProductInstanceCreatorState";
import { PProductUnavailabilityDetails } from "./PProductInstanceUnavailabilityDetails";
export class PProductInstanceCreator {
    constructor(params) {
        this.domain = params.domain;
        this.localizationProvider = params.localizationProvider;
        this.restaurant = params.restaurant;
        this.possibleProductTypes = params.possibleProductTypes;
        this._quantity = params.quantity;
        this._state = this.createState();
        makeAutoObservable(this);
    }
    static create(params) {
        const possibleProductTypes = new RArray(params.domain.possibleProductTypeIds)
            .map((possibleProductTypeId) => params.restaurant.productTypes.get(possibleProductTypeId))
            .sorted((a, b) => naturalCompare(a.name, b.name)).raw;
        return new PProductInstanceCreator({
            domain: params.domain,
            localizationProvider: params.localizationProvider,
            restaurant: params.restaurant,
            quantity: Quantity.One,
            possibleProductTypes,
        });
    }
    commonModifierOrder(productTypeIds) {
        const restaurantProductTypes = this.restaurant.productTypes;
        return productTypeIds
            .flatMap((productTypeId) => restaurantProductTypes.get(productTypeId).divisibleModifiersOrder)
            .filter((modifierTypeId, index, array) => array.indexOf(modifierTypeId) === index);
    }
    createState() {
        const parts = this.domain.state;
        const commonConfigCreator = PProductConfigCreator.create({
            domain: parts.commonConfigCreator,
            restaurant: this.restaurant,
            localizationProvider: this.localizationProvider,
            modifierOrder: this.commonModifierOrder(parts.productTypeIds),
        });
        if (parts.isSplit) {
            const firstHalf = this.partCreator(parts.firstHalf);
            const secondHalf = this.partCreator(parts.secondHalf);
            return new PHalvesProductInstanceCreator({
                commonConfigCreator,
                firstHalf,
                secondHalf,
            });
        }
        else {
            const part = this.partCreator(parts.partCreator);
            return new PSinglePartProductInstanceCreator({
                commonConfigCreator,
                part,
            });
        }
    }
    get state() {
        return this._state;
    }
    partCreator(part) {
        return PProductInstanceCreatorPart.create({
            domain: part,
            productTypeId: part.productTypeId,
            possibleProductTypes: this.possibleProductTypes,
            restaurant: this.restaurant,
            localizationProvider: this.localizationProvider,
        });
    }
    get product() {
        return this.state.product;
    }
    get isSplitPossible() {
        return this.domain.isSplitPossible;
    }
    split() {
        this.domain.split();
        this._state = this.createState();
    }
    removeFirstHalf() {
        this.domain.removeFirstHalf();
        this._state = this.createState();
    }
    removeSecondHalf() {
        this.domain.removeSecondHalf();
        this._state = this.createState();
    }
    setFirstHalfProductType(productTypeString) {
        const productType = this.restaurant.domain.menu.productTypes.rawGet(productTypeString);
        this.domain.setFirstHalfProductType(productType);
        this._state = this.createState();
    }
    setSecondHalfProductType(productTypeString) {
        const productType = this.restaurant.domain.menu.productTypes.rawGet(productTypeString);
        this.domain.setSecondHalfProductType(productType);
        this._state = this.createState();
    }
    get instance() {
        return this.domain.instance;
    }
    /**
     * Returns the total price (taking quantity into account)
     */
    get price() {
        return new PMoney(this.localizationProvider, this.instance.price.multiply(this._quantity).spectrum).dualPriceValue;
    }
    get availability() {
        return this.instance.availability;
    }
    get error() {
        const parts = this.domain.state.productInstanceParts;
        if (parts.type === "Halves" &&
            parts.availability.unavailabilityReasons.includes("HalvingAvailability")) {
            const { customParametersDetails } = new PProductUnavailabilityDetails({
                availability: this.availability,
                restaurant: this.restaurant,
            });
            if (customParametersDetails !== null) {
                return PProductError.notDivisible(customParametersDetails);
            }
        }
        return PProductError.none();
    }
    get isSubmitVisible() {
        return !this.state.isTemporaryUnavailable;
    }
    get isCancelVisible() {
        return this.state.isTemporaryUnavailable;
    }
    /** See {@link Creator.fixAvailability} */
    fixAvailability() {
        this.domain.fixAvailability();
    }
    /**
     * Dump state as JSON for debugging
     */
    debug() {
        return this.instance.checkoutData;
    }
    touch() {
        this.state.touch();
    }
    /**
     * If successful, returns the produced ProductInstance, otherwise null.
     */
    finish() {
        this.touch();
        // TODO: add test case for this
        if (this.availability.isAvailable.isDefinitelyFalse) {
            return null;
        }
        return this.instance;
    }
    get quantity() {
        return this._quantity.value;
    }
    get canDecrement() {
        return this._quantity.value > 1;
    }
    get canIncrement() {
        return true;
    }
    increment() {
        if (this.canIncrement) {
            this._quantity = this._quantity.incremented();
        }
    }
    decrement() {
        if (this.canDecrement) {
            this._quantity = this._quantity.decremented();
        }
    }
}
